import { Grid } from '@mui/material';
import { Box } from '@mui/system';
import { Form, Formik } from 'formik';
import RegisterIdCardForm from 'front-office/components/registration-id-card';
import { RegisterIdCardCrumbs } from 'front-office/constants/register-id-card-crumbs';
import { RegistationIdCardFormVM } from 'front-office/view-models/registration-id-card';
import { Fragment, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { StepTypography, StepButton } from 'shell/components/shared/step-button';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { PmiButton } from 'shell/components/shared/button';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { validationSchemaRegistrationIdCard } from 'front-office/constants/registration-id-card-validation-schema';
import { transformResponse } from 'front-office/utils/transformer-registration-id-card';
import ExitModalRegCancIdCard from 'front-office/components/shared/exit-modal';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';

const RegisterIdCardPage = (): React.ReactElement => {
  const [queryParams] = useSearchParams();

  const [isCancellationModalOpen, setCancellationModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  const { userProfile } = useUserProfile();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, RegistationIdCardFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: (response) => transformResponse(response),
    }
  );

  const registerIdCard = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carte-identita/registrazione-cid`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const onSubmit = useCallback(
    (form: RegistationIdCardFormVM) => {
      const { person, identityCard, dataRilascio3002 } = form;

      (
        registerIdCard({
          data: {
            generalitaRichiedente: form.generality,
            cartaIdentita: {
              matricola: person?.generality?.idSchedaSoggettoComune?.idSchedaSoggetto
                ? Number.parseInt(person?.generality?.idSchedaSoggettoComune?.idSchedaSoggetto)
                : undefined,
              key: {
                numero: identityCard?.number,
                ente: userProfile?.datiLogin?.key?.codiceEnte,
              },
              dataRilascio: identityCard?.releaseDate?.toFormat('yyyyMMdd'),
              dataScadenza: identityCard?.expirationDate?.toFormat('yyyyMMdd'),
              flag: identityCard?.type,
              motivoRilascio: identityCard.releaseMode,
              servizio: userProfile?.datiLogin?.profile?.servizio?.key?.codiceServizio,
              validita: identityCard.expatriationBan ? 'S' : 'N',
            },
            comuneRilascio: {
              codiceIstat: identityCard.municipality?.id,
              descrizioneLocalita: identityCard.location || undefined,
              nomeComune: identityCard.municipality?.description,
              siglaProvinciaIstat: identityCard.municipality?.province,
            },
            consolatoRilascio: {
              codiceConsolato: identityCard.consulateCode,
            },
            daContabilizzare: identityCard.toAccount,
            tipoPagamento: identityCard.typePayment,
            dataRilascio3002,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sportello/registrazione-carta');
      });
    },
    [navigate, registerIdCard, userProfile]
  );

  const openCancellationModal = useCallback(
    () => setCancellationModalOpen(true),
    [setCancellationModalOpen]
  );

  const closeCancellationModal = useCallback(
    () => setCancellationModalOpen(false),
    [setCancellationModalOpen]
  );

  return (
    <Fragment>
      <Breadcrumbs items={RegisterIdCardCrumbs} />
      {!anpr.data || anpr.isLoading || anpr.pristine ? undefined : (
        <Formik
          initialValues={anpr.data as RegistationIdCardFormVM}
          onSubmit={onSubmit}
          validationSchema={validationSchemaRegistrationIdCard}
        >
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <StickyDiv>
                  <StepButton active fullWidth>
                    <StepTypography active>Documenti</StepTypography>
                  </StepButton>
                  <div className="pmi-pt-4">
                    <div className="pmi-pb-2">
                      <StepButton
                        variant="outlined"
                        color="cdmBrandThird"
                        fullWidth
                        isButton
                        type="submit"
                        id="confirm"
                      >
                        <Box
                          sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Conferma
                        </Box>
                      </StepButton>
                    </div>
                    <div className="pmi-pb-2">
                      <PmiButton
                        variant="contained"
                        color="cdmBrand"
                        fullWidth
                        type="button"
                        onClick={openCancellationModal}
                        id="cancel-operattion"
                      >
                        <Box
                          sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Annulla Operazione
                        </Box>
                      </PmiButton>
                    </div>
                  </div>
                </StickyDiv>
              </Grid>
              <Grid item xs={12} md={10}>
                {anpr.data ? <RegisterIdCardForm /> : undefined}
              </Grid>
            </Grid>
            <ExitModalRegCancIdCard
              onClose={closeCancellationModal}
              onConfirm={() => navigate('/sportello/registrazione-carta')}
              open={isCancellationModalOpen}
            />
          </Form>
        </Formik>
      )}
    </Fragment>
  );
};

RegisterIdCardPage.displayName = 'Register id card page component';

export default RegisterIdCardPage;
